import { Injectable } from '@angular/core';
import { ConnectionStatus } from '@modeso/aumico-lib-core-fe';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { bexioActions } from '../actions/bexio.actions';
import {
    selectBexioBusinessYears,
    selectBexioBusinessYearsLoading,
    selectBexioConnection,
    selectBexioConnectionStatus,
    selectBexioImportError,
    selectBexioImportResult,
    selectBexioImportStatus,
    BexioState,
} from '../reducer/bexio.reducer';
import { BexioBusinessYear, BexioConnectionStatusDto, BexioImportResult } from '../services/bexio.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class BexioProvider {
    public constructor(private readonly store: Store<BexioState>) {}

    // ---- Dispatch ----

    public loadConnection(companyId: string): void {
        this.store.dispatch(bexioActions.loadConnection({ companyId }));
    }

    public loadBusinessYears(companyId: string): void {
        this.store.dispatch(bexioActions.loadBusinessYears({ companyId }));
    }

    public connect(companyId: string): void {
        this.store.dispatch(bexioActions.connect({ companyId }));
    }

    public disconnect(companyId: string): void {
        this.store.dispatch(bexioActions.disconnect({ companyId }));
    }

    public importData(companyId: string, annualReportId: string, businessYearId: number, previousBusinessYearId?: number, annualReportMetaInfoId?: string): void {
        this.store.dispatch(bexioActions.importData({ companyId, annualReportId, businessYearId, previousBusinessYearId, annualReportMetaInfoId }));
    }

    public resetImport(): void {
        this.store.dispatch(bexioActions.resetImport());
    }

    public cancelConnect(): void {
        this.store.dispatch(bexioActions.cancelConnect());
    }

    // ---- Select ----

    public getConnection$(): Observable<BexioConnectionStatusDto | null> {
        return this.store.select(selectBexioConnection);
    }

    public getConnectionStatus$(): Observable<BexioState['connectionStatus']> {
        return this.store.select(selectBexioConnectionStatus);
    }

    /**
     * Bexio's connection status mapped onto the shared `ConnectionStatus` enum so
     * UI that renders multiple vendors (e.g. the company integrations overview)
     * speaks ONE status vocabulary. Transient UI states (`idle`/`loading`) map to
     * `null` — they are not a persisted connection lifecycle state.
     */
    public getNormalizedConnectionStatus$(): Observable<ConnectionStatus | null> {
        return this.store.select(selectBexioConnectionStatus).pipe(map(toConnectionStatus));
    }

    public getBusinessYears$(): Observable<BexioBusinessYear[]> {
        return this.store.select(selectBexioBusinessYears);
    }

    public getBusinessYearsLoading$(): Observable<boolean> {
        return this.store.select(selectBexioBusinessYearsLoading);
    }

    public getImportStatus$(): Observable<BexioState['importStatus']> {
        return this.store.select(selectBexioImportStatus);
    }

    public getImportResult$(): Observable<BexioImportResult | null> {
        return this.store.select(selectBexioImportResult);
    }

    public getImportError$(): Observable<HttpErrorResponse | null> {
        return this.store.select(selectBexioImportError);
    }
}

/**
 * Maps Bexio's local connection-status string union onto the shared
 * `ConnectionStatus` enum. `idle`/`loading` are transient UI states, not a
 * persisted lifecycle status, so they map to `null`.
 */
function toConnectionStatus(status: BexioState['connectionStatus']): ConnectionStatus | null {
    switch (status) {
        case 'connected':
            return ConnectionStatus.CONNECTED;
        case 'expired':
            return ConnectionStatus.EXPIRED;
        case 'error':
            return ConnectionStatus.ERROR;
        default:
            return null;
    }
}
